import { Injectable } from '@angular/core';
import { environment } from "../../environments/environment";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, EMPTY, Observable, throwError } from "rxjs";
import { TableListRequest } from "../models/table-list-request.interface";
import { Charge } from "../models/charge.interface";
import { ChargeGroup } from "../models/charge-group";
import { StatsResponse } from "../models/stats-response.interface";
import { APIResponse } from "../models/standard-response.interface";
import { TariffRuleSet } from "../models/tariff-rule-set";
import { Invoice } from "../models/invoice.interface";
import { ChargeCalculationRequest } from "../models/charge-calculation-request";
import { CreditNote } from "../models/credit-note";
import { PaginatedListDataResponse } from "../models/paginated-list-data-response";
import { PermissionService } from "./permission.service";
import { PermissionConstants } from "../constants/permission-constants";
import { RequiresPermissions } from "../decorators/evealuate-permission.decorator";
import { AgreementConfigurationDTO, CustomerAgreementInterface } from "../models/customer-agreement.interface";

const chargesEndPoint = "v1/charges";
const chargeGroupsEndPoint = "v1/charge-groups";
const determinantsEndPoint = "v1/determinants";
const tariffsEndPoint = "v1/tariffs";
const invoiceEndPoint = "v1/invoices";
const billingEndPoint = "v1/billing";
const paymentsEndPoint = "v1/payments";
const creditNoteEndPoint = "v1/credit_notes"
const customerAgreementEndPoint = "v1/customer-agreements";

@Injectable({
  providedIn: 'root'
})
export class FinanceApiService {
  private baseUrl: string = environment.apiBaseUrl;
/*  private invoicePdf = new BehaviorSubject<any>({
    pdfSrc: "",
  });*/

  constructor(private http: HttpClient,private permissionService: PermissionService) {
  }

/*  getInvoicePdfSource(): Observable<any> {
    return this.invoicePdf.asObservable();
  }

  setInvoicePdfSource(pdfSrc: string) {
    this.invoicePdf.next({ pdfSrc });
  }

  clearInvoicePdfSource() {
    this.invoicePdf.next(null);
  }*/

  @RequiresPermissions({ permissionIds: [PermissionConstants.CHARGE.LIST] })
  getChargesStats(): Observable<any> {
    return this.http.get(`${this.baseUrl}/${chargesEndPoint}/stats`);
  }

  @RequiresPermissions({ permissionIds: [PermissionConstants.CHARGE.LIST] })
  getChargesList(chargeListRequest: TableListRequest): Observable<any> {
    console.log(chargeListRequest);
    return this.http.post(
      `${this.baseUrl}/${chargesEndPoint}/list`,
      chargeListRequest
    );
  }

  getChargesById(chargeId: string): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/${chargesEndPoint}/${chargeId}`
    );
  }

  createCharge(charge: Charge) {
    console.log("creating charge" + charge.name)
    return this.http.post(
      `${this.baseUrl}/${chargesEndPoint}`,
      charge
    );
  }

  updateCharge(charge: Charge, chargeId: string) {
    console.log("updating charge" + charge.name)
    return this.http.put(
      `${this.baseUrl}/${chargesEndPoint}/${chargeId}`,
      charge
    );
  }


  setChargeActiveStatus(shouldSetActive: boolean, idList: string[]) {
    return this.http.patch(
      `${this.baseUrl}/${chargesEndPoint}/status?status=${shouldSetActive}`,
      idList
    );
  }

  isValidSymbol(symbol: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/${determinantsEndPoint}/validate-symbol?symbol=${symbol}`);
  }

  createChargeGroup(chargeGroup: ChargeGroup) {
    return this.http.post(
      `${this.baseUrl}/${chargeGroupsEndPoint}`,
      chargeGroup
    );
  }

  @RequiresPermissions({ permissionIds: [PermissionConstants.CHARGE_GROUP.LIST] })
  getChargeGroupStats():Observable<any> {
    return this.http.get(`${this.baseUrl}/${chargeGroupsEndPoint}/stats`);
  }

  @RequiresPermissions({ permissionIds: [PermissionConstants.CHARGE_GROUP.LIST] })
  getChargeGroupList(tableListRequest: TableListRequest) {
    return this.http.post(
      `${this.baseUrl}/${chargeGroupsEndPoint}/list`,
      tableListRequest
    );
  }

  setChargeGroupActiveStatus(flag: boolean, selectedItems: string[]) {
    return this.http.patch(
      `${this.baseUrl}/${chargeGroupsEndPoint}/status?status=${flag}`,
      selectedItems
    );
  }

  getChargeGroupById(chargeGroupId: string) {
    return this.http.get(
      `${this.baseUrl}/${chargeGroupsEndPoint}/${chargeGroupId}`
    );
  }

  updateChargeGroup(formValue: ChargeGroup, chargeGroupId: string) {
    return this.http.put(
      `${this.baseUrl}/${chargeGroupsEndPoint}/${chargeGroupId}`,
      formValue
    );
  }

  addChargesToChargeGroup(chargeIds: string[],chargeGroupId: string) {
    return this.http.put(
      `${this.baseUrl}/${chargeGroupsEndPoint}/${chargeGroupId}/mappings`,
      chargeIds
    );
  }

  addChargeGroupToCharge(selectedChargeGroupItems: { chargeGroupsAdded: string[]; chargeGroupsRemoved: string[] },chargeId: string) {
    return this.http.patch(
      `${this.baseUrl}/${chargesEndPoint}/${chargeId}/charge-groups`,
      selectedChargeGroupItems
    )
  }

  listTariffs(tableListRequest: TableListRequest) {
    return this.http.post(
      `${this.baseUrl}/${tariffsEndPoint}/list`,
      tableListRequest
    );
  }

  // getDeterminantDetails(determinantType?: string, chargeId?: string | { key: string }, selectedDeterminant?: string): Observable<APIResponse> {
  //   const actualChargeId = typeof chargeId === 'object' ? chargeId?.key : chargeId;
  //   return this.http.get<APIResponse>(`${this.baseUrl}/${determinantsEndPoint}/details${actualChargeId ? `?chargeId=${actualChargeId}` : ''}`);
  // }

  getDeterminantDetails(determinantType: string, chargeId?: string | { key: string }, selectedDeterminant?: string): Observable<APIResponse> {
    const actualChargeId = typeof chargeId === 'object' ? chargeId?.key : chargeId;
  
    const params: string[] = [];
  
    if (actualChargeId) {
      params.push(`chargeId=${actualChargeId}`);
    }
  
    if (determinantType) {
      params.push(`determinantType=${determinantType}`);
    }
  
    const queryString = params.length ? `?${params.join('&')}` : '';
  
    return this.http.get<APIResponse>(
      `${this.baseUrl}/${determinantsEndPoint}/details${queryString}`
    );
  }

  getDeterminantValues(determinantName: string, tableListRequest: TableListRequest) {
    return this.http.post(
      `${this.baseUrl}/${determinantsEndPoint}/values/${determinantName}`,
      tableListRequest
    );
  }

  saveTariff(rulesSets: any) {
    return this.http.post(
      `${this.baseUrl}/${tariffsEndPoint}`,
      rulesSets
    );
  }

  getTariffDetails(tariffId: string) {
    return this.http.get<APIResponse>(`${this.baseUrl}/${tariffsEndPoint}/${tariffId}`);
  }

  updateTariff(tariffId: string, saveTariffRequest: any) {
    return this.http.put(
      `${this.baseUrl}/${tariffsEndPoint}/${tariffId}`,
      saveTariffRequest
    );
  }

  deleteTariff(tariffId: string) {
    return this.http.delete(`${this.baseUrl}/${tariffsEndPoint}/${tariffId}`)
  }


  //INVOICES
  generateInvoice(invoiceGenerationRequest: Invoice): Observable<APIResponse> {
    return this.http.post<APIResponse>(`${this.baseUrl}/${invoiceEndPoint}`, invoiceGenerationRequest);
  }

  estimateInvoice(invoiceGenerationRequest: Invoice): Observable<APIResponse> {
    return this.http.put<APIResponse>(`${this.baseUrl}/${invoiceEndPoint}/estimate`, invoiceGenerationRequest);
  }

  fetchItemDetails(dto: { documentType: string; documentId: string }) {
    return this.http.post<APIResponse>(`${this.baseUrl}/${billingEndPoint}/items/fetch`, dto);
  }

  listInvoices(dataListRequest: TableListRequest) {
    return this.http.post(
      `${this.baseUrl}/${invoiceEndPoint}/list`,
      dataListRequest
    );

  }

  getInvoiceByIds(invoiceIds: string[]) {
    const commaSeparatedIds = invoiceIds.join(',');
    console.log(commaSeparatedIds);
    return this.http.get(`${this.baseUrl}/${invoiceEndPoint}?invoiceId=${commaSeparatedIds}`);
  }

  getInvoiceByCustomerId(customerId: string) {
    return this.http.get(`${this.baseUrl}/${invoiceEndPoint}/customer/${customerId}/pending`);
  }

  generateIRNForInvoice(invoiceId: string) {
    return this.http.post(`${this.baseUrl}/${invoiceEndPoint}/${invoiceId}/generate-irn`, null);
  }

  recordPayment(paymentRequest: any) {
    return this.http.post(`${this.baseUrl}/${paymentsEndPoint}`, paymentRequest);
  };

  getPaymentList(tableListRequest: TableListRequest): Observable<any> {
    return this.http.post(`${this.baseUrl}/${paymentsEndPoint}/list`, tableListRequest);
  }

  getPayment(paymentId: String): Observable<any> {
    return this.http.get(`${this.baseUrl}/${paymentsEndPoint}/${[paymentId]}`);
  }

  getEstimateForCharge(chargeCalculationRequest: ChargeCalculationRequest): Observable<any> {
    return this.http.post(`${this.baseUrl}/${invoiceEndPoint}/charges/estimate`, chargeCalculationRequest);
  }

  createCreditNote(creditNoteData: CreditNote) {
    return this.http.post(`${this.baseUrl}/${creditNoteEndPoint}`, creditNoteData);
  }

  @RequiresPermissions({ permissionIds: [PermissionConstants.CREDIT_NOTE.LIST] })
  listCreditNotes(tableListData: TableListRequest) {
    return this.http.post(`${this.baseUrl}/${creditNoteEndPoint}/list`, tableListData);
  }

  getCreditNoteById(creditNoteId: string) {
    return this.http.get(`${this.baseUrl}/${creditNoteEndPoint}/${creditNoteId}`);
  }

  getInvoiceDetails(invoiceId: string) {
    return this.http.get(`${this.baseUrl}/${invoiceEndPoint}/${invoiceId}`);
  }

  getInvoicePdf(invoiceId: string) {
    return this.http.get(`${this.baseUrl}/${invoiceEndPoint}/${invoiceId}/pdf`);
  }

  printInvoicePdf(invoiceId: string) {
    return this.http.get(`${this.baseUrl}/${invoiceEndPoint}/${invoiceId}/print-pdf`);
  }

  getPaymentsForInvoice(invoiceId: string) {
    return this.http.get(`${this.baseUrl}/${invoiceEndPoint}/${invoiceId}/payments`);
  }

  createCustomerAgreement(customerAgreementDto: CustomerAgreementInterface): Observable<APIResponse> {
    return this.http.post<APIResponse>(`${this.baseUrl}/${customerAgreementEndPoint}`, customerAgreementDto);
  }

  getCustomerAgreement(agreementId: string): Observable<APIResponse> {
    return this.http.get<APIResponse>(`${this.baseUrl}/${customerAgreementEndPoint}/${agreementId}`);
  }

  updateCustomerAgreement(currentAgreementId: string, customerAgreementDto: CustomerAgreementInterface) {
    return this.http.put<APIResponse>(`${this.baseUrl}/${customerAgreementEndPoint}/${currentAgreementId}`, customerAgreementDto)
  }

  createAgreementConfiguration(currentAgreementId: string, agreementConfigurationDto: AgreementConfigurationDTO): Observable<APIResponse> {
    return this.http.post<APIResponse>(`${this.baseUrl}/${customerAgreementEndPoint}/${currentAgreementId}/configurations`, agreementConfigurationDto);
  }

  @RequiresPermissions({ permissionIds: [PermissionConstants.CUSTOMER_AGREEMENT.LIST] })
  listCustomerAgreements(tableListRequest: TableListRequest): Observable<APIResponse> {
    return this.http.post<APIResponse>(`${this.baseUrl}/${customerAgreementEndPoint}/list`,tableListRequest);
  }

  getAgreementConfiguration(customerAgreementId: string, configurationId: string): Observable<APIResponse> {
    return this.http.get<APIResponse>(`${this.baseUrl}/${customerAgreementEndPoint}/${customerAgreementId}/configurations/${configurationId}`);
  }

  editAgreementConfiguration(customerAgreementId: string, configurationId: string, agreementConfigurationDTO: AgreementConfigurationDTO): Observable<APIResponse> {
    return this.http.put<APIResponse>(`${this.baseUrl}/${customerAgreementEndPoint}/${customerAgreementId}/configurations/${configurationId}`, agreementConfigurationDTO);
  }

  cloneCustomerAgreement(id: string, record: CustomerAgreementInterface) {
    return this.http.post<APIResponse>(`${this.baseUrl}/${customerAgreementEndPoint}/${id}/clone`, record);
  }

  cloneCustomerAgreementConfiguration(customerAgreementId: string, configurationId: string,agreementConfigurationDto: AgreementConfigurationDTO) {
    return this.http.post<APIResponse>(`${this.baseUrl}/${customerAgreementEndPoint}/${customerAgreementId}/configurations/${configurationId}/clone`,agreementConfigurationDto);
  }
}
